import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { VendorState } from "../../../../redux/vendor/rootReducer";
import { useTranslation } from "react-i18next";
import { BricksRequirements } from "../models/BricksRequirements.model";
import { useTextureApi } from "../../../../common/hooks/use-texture-api.hook";
import { setLoading } from "../../../../redux/vendor/ui/uiActions";
import { cn } from "@/lib/utils";
import {
    GroutColorField,
    PatternFieldLabel,
    PatternSectionTitle,
    SliderWithInput,
} from "./PatternFormControls";

// Miniature brick-layout glyphs for the offset picker tiles.
const brick = "bg-muted-foreground/30";
const OFFSET_PREVIEWS = {
  half: (
    <div className="grid h-full w-full grid-rows-2 gap-0.5">
      <div className="grid grid-cols-2 gap-0.5">
        <div className={brick} />
        <div className={brick} />
      </div>
      <div className="grid grid-cols-2 gap-0.5">
        <div className={cn(brick, "col-span-2")} />
      </div>
    </div>
  ),
  third: (
    <div className="grid h-full w-full grid-rows-2 gap-0.5">
      <div className="grid grid-cols-3 gap-0.5">
        <div className={brick} />
        <div className={brick} />
        <div className={brick} />
      </div>
      <div className="grid grid-cols-3 gap-0.5">
        <div className={cn(brick, "col-span-2")} />
        <div className={brick} />
      </div>
    </div>
  ),
  random: (
    <div className="grid h-full w-full grid-rows-2 gap-0.5">
      <div className="grid grid-cols-4 gap-0.5">
        <div className={cn(brick, "col-span-2")} />
        <div className={brick} />
        <div className={brick} />
      </div>
      <div className="grid grid-cols-4 gap-0.5">
        <div className={brick} />
        <div className={cn(brick, "col-span-3")} />
      </div>
    </div>
  ),
};

export const BricksPatternForm = ({seamlessResult, setSeamlessResult, uploadedImages = []}) => {
  const { t } = useTranslation();
  const { language } = useSelector((state: VendorState) => state.settings.theme);
  const [isOnEditGroutColor, setIsOnEditGroutColor] = useState(false);
  const textureAPI = useTextureApi();
  const dispatch = useDispatch();
  const [fields, setFields] = useState({
      plankWidth: {
          isTouched: false,
          label: `${t('PATTERNS.PARTIALS.PLANK_WIDTH')}`,
          name: "plankWidth",
          value: "10",
          unit: "cm"
      },
      plankHeight: {
          isTouched: false,
          label: `${t('PATTERNS.PARTIALS.PLANK_HEIGHT')}`,
          name: "plankHeight",
          value: "5",
          unit: "cm"
      },
      textureWidth: {
          isTouched: false,
          label: `${t('PATTERNS.PARTIALS.TEXTURE_WIDTH')}`,
          name: "textureWidth",
          value: "2",
          unit: "m"
      },
      textureHeight: {
          isTouched: false,
          label: `${t('PATTERNS.PARTIALS.TEXTURE_HEIGHT')}`,
          name: "textureHeight",
          value: "2",
          unit: "m"
      },
      groutLine: {
        isTouched: false,
        label: `${t('PATTERNS.PARTIALS.GROUT_LINE_WIDTH')}`,
        name: "groutLine",
        value: "1",
        unit: "mm"
      },
      offset: {
        isTouched: false,
        label: `${t('PATTERNS.PARTIALS.OFFSET')}`,
        name: "offset",
        value: "half",
      },
      groutColor: {
          isTouched: false,
          label: `${t('PATTERNS.PARTIALS.GROUT_LINE_COLOR')}`,
          name: "groutColor",
          value: "#CCCCCC"
      }
  });

  const handleChange = (event) => {
      const { name, value } = event.target;
      setFields({...fields, [name]: {
          ...fields[name],
          value: value,
          isTouched: true
      }});
  };

  const handleSliderChange = (name, value) => {
    setFields({...fields, [name]: {
      ...fields[name],
      value: value.toString(),
      isTouched: true
    }});
  };

  const isValidForm = () => {
    return uploadedImages && uploadedImages.length > 0 &&
      fields.plankWidth.value &&
      fields.plankHeight.value &&
      fields.offset.value &&
      fields.textureWidth.value &&
      fields.textureHeight.value;
  };

  const handleGenerateSeamless = async () => {
    if(isValidForm()){
      try {
        dispatch(setLoading(true));
        const body = new BricksRequirements();
        body.setFiles(uploadedImages)
            .setPlankWidth(fields.plankWidth.value)
            .setPlankHeight(fields.plankHeight.value)
            .setTextureWidth(fields.textureWidth.value)
            .setTextureHeight(fields.textureHeight.value)
            .setGroutLineColor(fields.groutColor.value || "#FFFFFF")
            .setGroutLineWidth(fields.groutLine.value)
            .setOffset(fields.offset.value);

        let data = await textureAPI.generateTextureForPattern(body, "bricks");
        setSeamlessResult(data);
      } catch (error) {
        console.error('Error generating seamless texture:', error);
        setSeamlessResult(null);
      } finally {
        dispatch(setLoading(false));
      }
    }
  };

  const handleGroutColorChange = (color) => {
    setFields({
      ...fields,
      groutColor: {
        ...fields.groutColor,
        value: color.hex + Math.round(color.rgb.a * 255).toString(16)
      }
    });
  };

  return (
    <div>
      {/* Hidden generate button that can be triggered programmatically */}
      <button
        type="button"
        id="bricks-generate-btn"
        onClick={handleGenerateSeamless}
        className="hidden"
      />

      <div className="mb-6">
        <PatternSectionTitle>{t('PATTERNS.PARTIALS.PATTERN_SETTINGS')}</PatternSectionTitle>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          {(['half', 'third', 'random'] as const).map((value) => {
            const selected = fields.offset.value === value;
            return (
              <button
                type="button"
                key={value}
                onClick={() => handleChange({ target: { name: 'offset', value } })}
                className={cn(
                  'flex h-full cursor-pointer flex-col items-center justify-center border p-3 transition-colors',
                  selected
                    ? 'border-foreground bg-muted'
                    : 'border-border bg-card rounded-xl hover:border-foreground/40 hover:bg-muted/50'
                )}
              >
                <div className="mb-2 flex h-[60px] w-20 items-center justify-center p-1">
                  {OFFSET_PREVIEWS[value]}
                </div>
                <span
                  className={cn(
                    'text-center text-xs font-medium',
                    selected ? 'text-foreground' : 'text-muted-foreground'
                  )}
                >
                  {t(`PATTERNS.PARTIALS.BRICKS_${value.toUpperCase()}`)}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="mt-8 mb-6">
        <PatternSectionTitle>{t('PATTERNS.PARTIALS.DIMENSIONS')}</PatternSectionTitle>

        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <div>
            <PatternFieldLabel>{fields.plankWidth.label}</PatternFieldLabel>
            <SliderWithInput
              name="plankWidth"
              value={fields.plankWidth.value}
              unit={fields.plankWidth.unit}
              min={5}
              max={50}
              step={1}
              onSliderChange={handleSliderChange}
              onInputChange={handleChange}
            />
          </div>

          <div>
            <PatternFieldLabel>{fields.plankHeight.label}</PatternFieldLabel>
            <SliderWithInput
              name="plankHeight"
              value={fields.plankHeight.value}
              unit={fields.plankHeight.unit}
              min={2}
              max={30}
              step={1}
              onSliderChange={handleSliderChange}
              onInputChange={handleChange}
            />
          </div>
        </div>
      </div>

      <div className="mt-8 mb-6">
        <PatternSectionTitle>{t('PATTERNS.PARTIALS.TEXTURE')}</PatternSectionTitle>

        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <div>
            <PatternFieldLabel>{fields.textureWidth.label}</PatternFieldLabel>
            <SliderWithInput
              name="textureWidth"
              value={fields.textureWidth.value}
              min={1}
              max={4}
              step={1}
              markLabels={['1m', '2m', '3m', '4m']}
              showInput={false}
              onSliderChange={handleSliderChange}
            />
          </div>

          <div>
            <PatternFieldLabel>{fields.textureHeight.label}</PatternFieldLabel>
            <SliderWithInput
              name="textureHeight"
              value={fields.textureHeight.value}
              min={1}
              max={4}
              step={1}
              markLabels={['1m', '2m', '3m', '4m']}
              showInput={false}
              onSliderChange={handleSliderChange}
            />
          </div>
        </div>
      </div>

      <div className="mt-8">
        <PatternSectionTitle>{t('PATTERNS.PARTIALS.GROUT')}</PatternSectionTitle>

        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <div>
            <PatternFieldLabel>{fields.groutLine.label}</PatternFieldLabel>
            <SliderWithInput
              name="groutLine"
              value={fields.groutLine.value}
              unit={fields.groutLine.unit}
              min={0}
              max={10}
              step={0.5}
              onSliderChange={handleSliderChange}
              onInputChange={handleChange}
            />
          </div>

          <div>
            <PatternFieldLabel>{fields.groutColor.label}</PatternFieldLabel>
            <GroutColorField
              color={fields.groutColor.value}
              open={isOnEditGroutColor}
              onOpenChange={setIsOnEditGroutColor}
              onColorChange={handleGroutColorChange}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
